import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Search, CheckCircle2, LogOut } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { pub, client, getToken, setToken, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

function PinGate({ onOk }) {
  const [pin, setPin] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await pub.post("/checkin/login", { pin }); setToken("checkin", data.token); onOk(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="card-warm max-w-md mx-auto space-y-5 mt-10" data-testid="checkin-pin-form">
      <Lock className="w-10 h-10 text-[#8C3415]" />
      <h1 className="text-3xl font-bold text-amber-900">Check-in desk</h1>
      <p className="text-lg">Enter the check-in PIN from your organizers.</p>
      <input data-testid="admin-pin-checkin-input" type="password" inputMode="numeric" className="field text-center !text-3xl tracking-[0.4em]" value={pin} onChange={(e) => setPin(e.target.value)} />
      <button data-testid="checkin-pin-submit" className="btn-primary w-full">Open check-in</button>
    </form>
  );
}

function FamilyRow({ f, onToggle }) {
  return (
    <li data-testid={`checkin-family-${f.id}`} className={`card-warm !p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${f.checked_in ? "!bg-green-50 !border-green-300" : ""}`}>
      <div className="flex-1 min-w-0">
        <p className="font-heading text-2xl font-semibold">{f.family_name}</p>
        <p className="text-stone-700 text-lg">{f.members.map((m) => m.name).join(", ")}</p>
        <div className="flex flex-wrap items-center gap-3 mt-2"><StatusBadge status={f.pay_status} testId={`checkin-status-${f.id}`} />
          {f.balance > 0 && <span className="font-semibold text-amber-900">Owes {money(f.balance)}</span>}</div>
      </div>
      <button data-testid={`checkin-toggle-${f.id}`} onClick={() => onToggle(f)} className={f.checked_in ? "btn-outline" : "btn-secondary"}>
        {f.checked_in ? <><CheckCircle2 className="w-6 h-6 text-green-700" />Checked in (undo)</> : "Check in"}
      </button>
    </li>
  );
}

export default function CheckIn() {
  const [ok, setOk] = useState(!!(getToken("checkin") || getToken("admin")));
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const kind = getToken("checkin") ? "checkin" : "admin";

  const load = useCallback(async () => {
    try { setList((await client(kind).get("/checkin/families")).data); }
    catch { setToken("checkin", null); setOk(false); }
  }, [kind]);
  useEffect(() => { if (ok) load(); }, [ok, load]);

  const toggle = async (f) => {
    try { await client(kind).post(`/checkin/families/${f.id}`, { checked_in: !f.checked_in }); setList((l) => l.map((x) => (x.id === f.id ? { ...x, checked_in: !f.checked_in } : x))); toast.success(f.checked_in ? "Check-in undone" : `${f.family_name} checked in`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shown = list.filter((f) => `${f.family_name} ${f.contact_name} ${f.members.map((m) => m.name).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  const done = list.filter((f) => f.checked_in).length;

  return (
    <div className="min-h-screen bg-[#FAF6F0] px-4 sm:px-8 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6"><Link to="/" className="font-heading text-xl font-bold text-[#8C3415]">Fanene ʻĀiga</Link>
          {ok && getToken("checkin") && <button data-testid="checkin-lock-button" onClick={() => { setToken("checkin", null); setOk(false); }} className="btn-outline"><LogOut className="w-5 h-5" />Lock</button>}</div>
        {!ok ? <PinGate onOk={() => setOk(true)} /> : (
          <div className="space-y-6">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-amber-950">Check-in</h1>
            <p data-testid="checkin-count" className="text-xl"><b>{done}</b> of <b>{list.length}</b> families checked in</p>
            <div className="relative"><Search className="w-6 h-6 absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" />
              <input data-testid="checkin-search-input" className="field !pl-12" placeholder="Search a family or person" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <ul className="space-y-4">{shown.map((f) => <FamilyRow key={f.id} f={f} onToggle={toggle} />)}</ul>
            {!shown.length && <p className="text-lg text-stone-600">No families found.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
